import React, { useEffect, useState } from "react";
import { PencilIcon, UsersIcon } from "lucide-react";
import { supabase } from "../../../../lib/supabaseClient";
import { getTournamentTheme } from "../../../../styles/themes";

export default function TournamentPlayers({
    tournament,
    updateTournament,
    isAdmin,
    setActiveTab,
}) {
    const [players, setPlayers] = useState([]);
    const [editing, setEditing] = useState({});
    const [teams, setTeams] = useState([]);
    const theme = getTournamentTheme(tournament?.theme_key);

    useEffect(() => {
        if (tournament?.id) {
            fetchPlayersAndTeams();
        }
    }, [tournament?.id]);

    const fetchPlayersAndTeams = async () => {
        const { data, error } = await supabase
            .from("tournament_players")
            .select("id, team_id, player:player_id(*)")
            .eq("tournament_id", tournament.id);

        const { data: teamsData } = await supabase
            .from("teams")
            .select("*")
            .eq("tournament_id", tournament.id);

        if (!error) {
            setPlayers(data || []);
            setTeams(teamsData || []);
        } else {
            console.error("Could not load players", error);
        }
    };

    const handlePlayerChange = (id, field, value) => {
        setPlayers((prev) =>
            prev.map((p) =>
                p.player.id === id
                    ? {
                          ...p,
                          player: {
                              ...p.player,
                              [field]: value,
                          },
                      }
                    : p
            )
        );
    };

    const toggleEdit = (id) => {
        setEditing((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const handleTeamChange = (playerId, newTeam) => {
        const updatedAssignments = {
            ...(tournament.team_data?.assignments || {}),
            [playerId]: newTeam,
        };

        const updatedTournament = {
            ...tournament,
            team_data: {
                ...(tournament.team_data || {}),
                assignments: updatedAssignments,
            },
        };

        updateTournament?.(updatedTournament);

        setPlayers((prev) =>
            prev.map((playerLink) =>
                playerLink.player.id === playerId
                    ? {
                          ...playerLink,
                          team_id: newTeam,
                      }
                    : playerLink
            )
        );
    };

    const handleSave = async () => {
        for (const playerLink of players) {
            const { error: playerError } = await supabase
                .from("players")
                .update({
                    name: playerLink.player.name,
                    handicap: playerLink.player.handicap,
                })
                .eq("id", playerLink.player.id);

            if (playerError) {
                console.error("Update error", playerError);
                alert("Ett fel uppstod vid uppdatering.");
                return;
            }
        }

        const { error: teamError } = await supabase
            .from("tournaments")
            .update({
                team_data: tournament.team_data,
            })
            .eq("id", tournament.id);

        if (teamError) {
            console.error("Team update error", teamError);
            alert("Fel vid laguppdatering.");
            return;
        }

        alert("✅ Spelare och lag uppdaterade!");
        setActiveTab("standings");
    };

    const getInitials = (name = "") => {
        const initials = name
            .trim()
            .split(/\s+/)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase())
            .join("");

        return initials || "?";
    };

    const getTeamDetails = (playerId) => {
        const teamKey =
            tournament.team_data?.assignments?.[playerId] || "";

        if (teamKey === "red") {
            return {
                key: "red",
                name: tournament.team_data?.captains?.red || "Röd",
                color: "#9F3A38",
            };
        }

        if (teamKey === "green") {
            return {
                key: "green",
                name: tournament.team_data?.captains?.green || "Grön",
                color: "#2F6B4F",
            };
        }

        return {
            key: "",
            name: "Ej tilldelad",
            color: "#B8B09F",
        };
    };

    return (
        <section
            className={`${theme.section} ${theme.border} overflow-hidden rounded-2xl border shadow-sm`}
        >
            <div
                className={`${theme.border} flex flex-col gap-3 border-b px-4 py-5 sm:flex-row sm:items-end sm:justify-between sm:px-6`}
            >
                <div className="flex items-center gap-3">
                    <span
                        className={`${theme.primarySoft} ${theme.heading} flex h-10 w-10 shrink-0 items-center justify-center rounded-full`}
                    >
                        <UsersIcon className="h-5 w-5" />
                    </span>

                    <div>
                        <p
                            className={`${theme.textSecondary} text-[10px] font-semibold uppercase tracking-[0.24em]`}
                        >
                            Startfält
                        </p>

                        <h2
                            className={`${theme.heading} mt-0.5 text-xl font-semibold tracking-tight sm:text-2xl`}
                        >
                            Spelare
                        </h2>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <span
                        className={`${theme.primarySoft} ${theme.heading} rounded-full px-3 py-1.5 text-xs font-semibold`}
                    >
                        {players.length} spelare
                    </span>

                    {tournament.team_mode && (
                        <span
                            className={`${theme.accentSoft} ${theme.accent} rounded-full px-3 py-1.5 text-xs font-semibold`}
                        >
                            Lagtävling
                        </span>
                    )}

                    {isAdmin && (
                        <span
                            className={`${theme.sectionMuted} ${theme.border} ${theme.textSecondary} rounded-full border px-3 py-1.5 text-xs font-medium`}
                        >
                            Redigeringsläge
                        </span>
                    )}
                </div>
            </div>

            {players.length === 0 ? (
                <div className="px-6 py-12 text-center">
                    <div
                        className={`${theme.primarySoft} ${theme.heading} mx-auto flex h-12 w-12 items-center justify-center rounded-full`}
                    >
                        <UsersIcon className="h-5 w-5" />
                    </div>

                    <h3
                        className={`${theme.heading} mt-4 text-lg font-semibold`}
                    >
                        Inga spelare hittades
                    </h3>

                    <p className={`${theme.textSecondary} mt-1 text-sm`}>
                        Turneringen saknar registrerade spelare.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 sm:p-5 lg:grid-cols-3">
                    {players.map((playerLink) => {
                        const player = playerLink.player;
                        const isEditing = Boolean(editing[player.id]);
                        const team = getTeamDetails(player.id);
                        const imageUrl =
                            player.image_url || player.image_yrl || null;

                        return (
                            <article
                                key={playerLink.id}
                                className={`${theme.tableRow} ${theme.border} relative min-w-0 overflow-hidden rounded-xl border shadow-[0_4px_14px_rgba(31,45,38,0.05)] transition duration-200 hover:-translate-y-0.5 hover:shadow-md`}
                            >
                                {tournament.team_mode && (
                                    <div
                                        className="absolute inset-y-0 left-0 w-1"
                                        style={{
                                            backgroundColor: team.color,
                                        }}
                                        aria-hidden="true"
                                    />
                                )}

                                <div className="p-4">
                                    <div className="flex items-start gap-3">
                                        {imageUrl ? (
                                            <img
                                                src={imageUrl}
                                                alt={player.name}
                                                className={`${theme.borderStrong} h-11 w-11 shrink-0 rounded-full border-2 object-cover`}
                                            />
                                        ) : (
                                            <div
                                                className={`${theme.primarySoft} ${theme.heading} flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold`}
                                                aria-label={`Initialer för ${player.name}`}
                                            >
                                                {getInitials(player.name)}
                                            </div>
                                        )}

                                        <div className="min-w-0 flex-1">
                                            {isEditing ? (
                                                <input
                                                    value={player.name}
                                                    onChange={(event) =>
                                                        handlePlayerChange(
                                                            player.id,
                                                            "name",
                                                            event.target.value
                                                        )
                                                    }
                                                    className={`${theme.section} ${theme.borderStrong} ${theme.textPrimary} w-full rounded-lg border px-2.5 py-1.5 text-base font-semibold outline-none transition focus:ring-2 focus:ring-[#183d2d]/20`}
                                                    aria-label="Spelarnamn"
                                                />
                                            ) : (
                                                <h3
                                                    className={`${theme.textPrimary} break-words text-base font-semibold leading-snug`}
                                                >
                                                    {player.name}
                                                </h3>
                                            )}

                                            <p
                                                className={`${theme.textSecondary} mt-0.5 truncate text-xs`}
                                                title={
                                                    player.home_club ||
                                                    "Klubb saknas"
                                                }
                                            >
                                                {player.home_club ||
                                                    "Klubb saknas"}
                                            </p>
                                        </div>

                                        {isAdmin && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    toggleEdit(player.id)
                                                }
                                                className={`${isEditing ? `${theme.primary} ${theme.textOnPrimary}` : `${theme.primarySoft} ${theme.heading}`} inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition hover:opacity-85`}
                                                aria-label={
                                                    isEditing
                                                        ? `Avsluta redigering av ${player.name}`
                                                        : `Redigera ${player.name}`
                                                }
                                                title={
                                                    isEditing
                                                        ? "Klar"
                                                        : "Redigera"
                                                }
                                            >
                                                <PencilIcon className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>

                                    <div
                                        className={`${theme.border} mt-4 grid grid-cols-2 gap-3 border-t pt-3`}
                                    >
                                        <div>
                                            <p
                                                className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.16em]`}
                                            >
                                                Handicap
                                            </p>

                                            {isEditing ? (
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    value={
                                                        player.handicap ?? ""
                                                    }
                                                    onChange={(event) =>
                                                        handlePlayerChange(
                                                            player.id,
                                                            "handicap",
                                                            event.target.value
                                                        )
                                                    }
                                                    className={`${theme.section} ${theme.borderStrong} ${theme.textPrimary} mt-1 w-20 rounded-lg border px-2 py-1.5 text-sm font-semibold outline-none transition focus:ring-2 focus:ring-[#183d2d]/20`}
                                                    aria-label={`Handicap för ${player.name}`}
                                                />
                                            ) : (
                                                <p
                                                    className={`${theme.heading} mt-1 text-lg font-semibold tabular-nums`}
                                                >
                                                    {player.handicap ?? "–"}
                                                </p>
                                            )}
                                        </div>

                                        <div className="min-w-0">
                                            <p
                                                className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.16em]`}
                                            >
                                                Klubb
                                            </p>

                                            <p
                                                className={`${theme.textPrimary} mt-1 truncate text-sm font-medium`}
                                                title={
                                                    player.home_club || "–"
                                                }
                                            >
                                                {player.home_club || "–"}
                                            </p>
                                        </div>
                                    </div>

                                    {tournament.team_mode && (
                                        <div
                                            className={`${theme.border} mt-3 border-t pt-3`}
                                        >
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p
                                                        className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.16em]`}
                                                    >
                                                        Lag
                                                    </p>

                                                    {!isAdmin && (
                                                        <div className="mt-1 flex items-center gap-2">
                                                            <span
                                                                className="h-2.5 w-2.5 shrink-0 rounded-full"
                                                                style={{
                                                                    backgroundColor:
                                                                        team.color,
                                                                }}
                                                            />
                                                            <span
                                                                className={`${theme.textPrimary} truncate text-sm font-semibold`}
                                                            >
                                                                {team.name}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                {isAdmin && (
                                                    <select
                                                        value={team.key}
                                                        onChange={(event) =>
                                                            handleTeamChange(
                                                                player.id,
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                        className={`${theme.sectionMuted} ${theme.border} ${theme.textPrimary} max-w-[170px] rounded-lg border px-2.5 py-2 text-xs font-semibold outline-none transition focus:ring-2 focus:ring-[#183d2d]/20`}
                                                        aria-label={`Lag för ${player.name}`}
                                                    >
                                                        <option value="">
                                                            Välj lag
                                                        </option>
                                                        <option value="red">
                                                            {tournament
                                                                .team_data
                                                                ?.captains
                                                                ?.red ||
                                                                "Röd"}
                                                        </option>
                                                        <option value="green">
                                                            {tournament
                                                                .team_data
                                                                ?.captains
                                                                ?.green ||
                                                                "Grön"}
                                                        </option>
                                                    </select>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {isAdmin && players.length > 0 && (
                <div
                    className={`${theme.sectionMuted} ${theme.border} flex justify-end border-t px-4 py-4 sm:px-6`}
                >
                    <button
                        type="button"
                        onClick={handleSave}
                        className={`${theme.primary} ${theme.primaryHover} ${theme.textOnPrimary} inline-flex items-center justify-center rounded-xl px-5 py-2.5 text-sm font-semibold shadow-sm transition`}
                    >
                        Spara spelare
                    </button>
                </div>
            )}
        </section>
    );
}